import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { instructorApi } from "../../services/instructorApi";
import { statusBadgeClass } from "../../components/instructor/InstructorShell";
import BrandMark from "../../components/BrandMark";
import Seo from "../../components/Seo";

export default function AdminApplicationDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError("");
      try {
        const res = await instructorApi.adminGet(id);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function reload() {
    setError("");
    try {
      const res = await instructorApi.adminGet(id);
      setData(res);
    } catch (err) {
      setError(err.message);
    }
  }

  async function act(action) {
    setBusy(true);
    setError("");
    try {
      if (action === "claim") await instructorApi.adminClaim(id);
      if (action === "approve") await instructorApi.adminApprove(id);
      if (action === "reject") await instructorApi.adminReject(id, reason);
      if (action === "suspend") await instructorApi.adminSuspend(id, reason);
      if (action === "reinstate") await instructorApi.adminReinstate(id);
      setReason("");
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (!data && !error) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  }

  const profile = data?.profile;

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Admin Application Review"
        description="Review an instructor application."
        canonicalPath={`/admin/applications/${id}`}
        robots="noindex,nofollow"
      />
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandMark />
            <span className="font-semibold text-gray-900">Admin review</span>
          </div>
          <Link to="/admin/applications" className="text-sm text-emerald-700 hover:underline">
            Back to queue
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
        {profile && (
          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 bg-white border border-gray-100 rounded-2xl p-6 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-3">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 border">
                    {profile.profile_image_url ? (
                      <img src={profile.profile_image_url} alt="" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                  <div>
                    <h1 className="text-xl font-semibold text-gray-900">{profile.display_name}</h1>
                    {profile.legal_name && profile.legal_name !== profile.display_name && (
                      <p className="text-sm text-gray-500">
                        Legal (private): <span className="font-medium text-gray-700">{profile.legal_name}</span>
                      </p>
                    )}
                    {profile.preferred_name && (
                      <p className="text-sm text-gray-500">Preferred: {profile.preferred_name}</p>
                    )}
                    <p className="text-gray-600">{profile.headline}</p>
                    <p className="text-sm text-gray-500">{profile.email}</p>
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadgeClass(profile.status)}`}>
                  {profile.status}
                </span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{profile.bio}</p>
              <div className="flex flex-wrap gap-2">
                {(profile.expertise || []).map((t) => (
                  <span key={t} className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-800">
                    {t}
                  </span>
                ))}
              </div>
              <div>
                <h2 className="font-medium text-gray-900 mb-2">Offerings</h2>
                <ul className="space-y-2 text-sm text-gray-700">
                  {(data.offerings || []).map((o) => (
                    <li key={o.id} className="border border-gray-100 rounded-lg p-3">
                      <div className="font-medium">{o.title}</div>
                      <div className="text-gray-500">
                        {o.listing_type} · {o.currency} {o.price} · {o.schedule_summary}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-3">
                <h2 className="font-semibold text-gray-900">Credentials</h2>
                {(data.credentials || []).map((c) => (
                  <div key={c.id} className="text-sm border-b border-gray-50 pb-3">
                    <p className="font-medium text-gray-900">{c.name}</p>
                    <p className="text-gray-500">
                      {[c.issuer, c.year_earned].filter(Boolean).join(" · ")}
                    </p>
                    {c.document_read_url && (
                      <a
                        href={c.document_read_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-700 hover:underline"
                      >
                        Open document
                      </a>
                    )}
                  </div>
                ))}
                {!data.credentials?.length && <p className="text-sm text-gray-500">No credentials</p>}
              </div>

              <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-3">
                <h2 className="font-semibold text-gray-900">Actions</h2>
                <p className="text-xs text-gray-500">Score: {profile.completeness_score}%</p>
                <textarea
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm min-h-[80px]"
                  placeholder="Reason required for reject / suspend"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  {profile.status === "Submitted" && (
                    <button type="button" disabled={busy} onClick={() => act("claim")} className="px-3 py-2 rounded-lg bg-amber-100 text-amber-900 text-sm font-medium">
                      Claim / under review
                    </button>
                  )}
                  {["Submitted", "UnderReview"].includes(profile.status) && (
                    <button type="button" disabled={busy} onClick={() => act("approve")} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium">
                      Approve
                    </button>
                  )}
                  {["Submitted", "UnderReview", "Approved"].includes(profile.status) && (
                    <button type="button" disabled={busy} onClick={() => act("reject")} className="px-3 py-2 rounded-lg bg-red-100 text-red-800 text-sm font-medium">
                      Reject / request changes
                    </button>
                  )}
                  {profile.status === "Approved" && (
                    <button type="button" disabled={busy} onClick={() => act("suspend")} className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-medium">
                      Suspend
                    </button>
                  )}
                  {profile.status === "Suspended" && (
                    <button type="button" disabled={busy} onClick={() => act("reinstate")} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium">
                      Reinstate
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-white border border-gray-100 rounded-2xl p-6">
                <h2 className="font-semibold text-gray-900 mb-2">History</h2>
                <ul className="space-y-2 text-xs text-gray-600 max-h-48 overflow-auto">
                  {(data.history || []).map((h) => (
                    <li key={h.id}>
                      {h.from_status || "—"} → {h.to_status}
                      {h.note ? ` · ${h.note}` : ""} · {new Date(h.created_at).toLocaleString()}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
